import dayjs from 'dayjs'
import { IGetUser, IUserLeaveTypeResult, IUserProbationPeriod } from '../../../types/custom-queries'
import { IDashboardLeaveRequest } from '@vacationtracker/shared/types/leave-request'
import { AlertBannerItem } from '../components/AlertBanner'
import { AccrualTypeEnum } from '@vacationtracker/shared/types/leave-policy'
import {
  getPayoutDateForBiWeeklyPeriod,
  getPayoutDateForWeeklyPeriod,
  getPayoutDateForSemiMonthlyPeriod,
  getPayoutDateForMonthlyPeriod
} from '@vacationtracker/shared/functions/calculation-shared'
import { getDatesForAccrualsMonthly } from '@vacationtracker/shared/functions/get-dates-for-accruals/get-dates-for-accruals-monthly'
import { getDatesForAccrualsBiWeekly } from '@vacationtracker/shared/functions/get-dates-for-accruals/get-dates-for-accruals-bi-weekly'
import { getDatesForAccrualsSemiMonthly } from '@vacationtracker/shared/functions/get-dates-for-accruals/get-dates-for-accruals-semi-monthly'
import { getDatesForAccrualsWeekly } from '@vacationtracker/shared/functions/get-dates-for-accruals/get-dates-for-accruals-weekly'
import getToAccrueDaysAndHours from '@vacationtracker/shared/functions/get-to-accrue-days-and-hours'
import { getAverageWorkingHours } from '@vacationtracker/shared/functions/work-week'
import { roundTo2Decimals, roundTo4Decimals } from '@vacationtracker/shared/functions/calculation-shared'
import { IFormatMessageFn } from '@vacationtracker/shared/types/display-in-days-hours'
import { openSupportChat } from '../../../util/open-support-chat'
import { IRolloverExpiry } from '..'

/**
 * Calculate quick stats from user leave data
 */
export const calculateQuickStats = (
  yearStart: string,
  yearEnd: string,
  leaveTypes: IUserLeaveTypeResult[],
  upcomingLeaves: IDashboardLeaveRequest[],
  pendingLeaves: IDashboardLeaveRequest[],
  user?: IGetUser,
  hourlyLeaveAccounting = false
) => {
  // Count actual requests this year from history + upcoming/pending (all statuses)
  const historyRequestsCount = user?.history?.filter(leave => dayjs(leave.startDate).isBetween(yearStart, yearEnd, 'day', '[]')).length || 0
  const upcomingRequestsCount = upcomingLeaves.filter(leave => dayjs(leave.startDate).isBetween(yearStart, yearEnd, 'day', '[]')). length
  const pendingRequestsCount = pendingLeaves.filter(leave => dayjs(leave.startDate).isBetween(yearStart, yearEnd, 'day', '[]')). length
  const totalRequestsThisYear = historyRequestsCount + upcomingRequestsCount + pendingRequestsCount
  
  const approvedLeaves = upcomingLeaves.filter(leave => leave.status === 'APPROVED')
  const averageLeaveLength = approvedLeaves.length > 0 
    ? approvedLeaves.reduce((sum, leave) => sum + leave.workingDays, 0) / approvedLeaves.length
    : 0

  const approvedRequests = approvedLeaves.length
  const pendingRequests = pendingLeaves.length

  // Get approver names from user data (handle multiple approvers)
  const approvers = user?.team?.approvers || []
  const approverName = approvers.length === 0 
    ? null 
    : approvers.map(approver => approver.name).join(', ')

  // Get next accrual date - calculate from the primary leave type with accruals
  const primaryLeaveTypeWithAccruals = leaveTypes.find(lt => 
    lt.isActive && lt.accrualType && lt.accrualType !== AccrualTypeEnum.none
  )
  
  const nextAccrualDate = primaryLeaveTypeWithAccruals && user?.location
    ? calculateNextAccrualDate(
      primaryLeaveTypeWithAccruals,
      user.location.yearStartDay || 1
    )
    : null

  const capHit = primaryLeaveTypeWithAccruals?.capHit
  const leaveTypeName = primaryLeaveTypeWithAccruals?.leaveTypeName
  
  // Calculate accrual amount for the next accrual
  const nextAccrual = nextAccrualDate && primaryLeaveTypeWithAccruals 
    ? (() => {
      const formattedDate = dayjs(nextAccrualDate).format('ddd, MMM DD, YYYY')
      
      // Calculate the accrual amount
      const userWorkingHours = user?.workHours?.workHours
      const averageWorkingHoursPerDay = userWorkingHours ? getAverageWorkingHours(userWorkingHours) : 8
      
      const { toAccrueDays, toAccrueHours } = getToAccrueDaysAndHours(
        hourlyLeaveAccounting,
        primaryLeaveTypeWithAccruals.accrualType,
        hourlyLeaveAccounting ? (
          primaryLeaveTypeWithAccruals.quotas.baseQuota.raw.hours
        ) : (
          primaryLeaveTypeWithAccruals.quotas.baseQuota.raw.days
        ),
        averageWorkingHoursPerDay
      )

      const accrualAmount = hourlyLeaveAccounting ? toAccrueHours : toAccrueDays
      const formattedAmount = hourlyLeaveAccounting 
        ? `+${roundTo2Decimals(accrualAmount)}h`
        : `+${roundTo4Decimals(accrualAmount)}d`
      
      return `${formattedDate} (${formattedAmount})`
    })()
    : null

  return {
    totalRequestsThisYear,
    averageLeaveLength,
    approvedRequests,
    pendingRequests,
    approverName,
    nextAccrual,
    capHit,
    leaveTypeName,
  }
}

/**
 * Generate alert banners based on user data
 */
export const generateAlertBanners = (
  leaveTypes: IUserLeaveTypeResult[],
  user: any,
  hourlyAccounting: boolean,
  rolloverExpiry: IRolloverExpiry | null,
  formatMessage: IFormatMessageFn
): AlertBannerItem[] => {
  const alerts: AlertBannerItem[] = []

  // Check for negative balances
  const negativeBalances = leaveTypes.filter(lt => 
    !lt.hasUnlimitedDays && lt.currentDays < 0
  )
  
  if (negativeBalances.length > 0) {
    alerts.push({
      id: 'negative-balance',
      type: 'error',
      title: formatMessage({ id: 'dashboard.alerts.negativeBalance.title' }),
      description: formatMessage(
        { id: 'dashboard.alerts.negativeBalance.description' },
        { leaveTypes: negativeBalances.map(lb => lb.leaveTypeName).join(', ') }
      ),
    })
  }

  // Check for expiring rollover days
  const expiringDays = rolloverExpiry ? rolloverExpiry.willExpire : 0
  const daysUntilExpiry = rolloverExpiry ? dayjs(rolloverExpiry.expiryDate).startOf('day').diff(dayjs(), 'day') : 1000 // we check for the next 60 days expiration only

  if (rolloverExpiry && expiringDays && daysUntilExpiry <= 60) {
    alerts.push({
      id: 'expiring-rollover',
      type: 'warning',
      title: formatMessage({ id: 'dashboard.alerts.expiringRollover.title' }),
      description: formatMessage(
        { id: hourlyAccounting ? 'dashboard.alerts.expiringRollover.descriptionHours' : 'dashboard.alerts.expiringRollover.descriptionDays' },
        { 
          amount: roundTo2Decimals(rolloverExpiry.willExpire || 0),
          leaveTypeName: rolloverExpiry.leaveTypeName,
          daysUntilExpiry,
        }
      ),
    })
  }

  // Check if user has no approvers
  if (!user.team?.approvers || user.team.approvers.length === 0) {
    alerts.push({
      id: 'no-approver',
      type: 'warning',
      title: formatMessage({ id: 'dashboard.alerts.noApprover.title' }),
      description: formatMessage({ id: 'dashboard.alerts.noApprover.description' }),
      action: {
        label: formatMessage({ id: 'dashboard.alerts.noApprover.action' }),
        onClick: () => openSupportChat(),
      },
    })
  }

  // Check for probation period restrictions
  if (user.probationPeriod && user.startDate) {
    const probationInfo = isUserInProbationPeriod(
      String(user.startDate), 
      Array.isArray(user.probationPeriod) ? user.probationPeriod : []
    )
    if (probationInfo.inProbation && probationInfo.probationEnd) {
      const endDate = dayjs(probationInfo.probationEnd)
      const daysRemaining = endDate.diff(dayjs(), 'days') + 1 // Include current day in the count
      
      alerts.push({
        id: 'probation-period',
        type: 'info',
        title: formatMessage({ id: 'dashboard.alerts.probationPeriod.title' }),
        description: formatMessage(
          { id: 'dashboard.alerts.probationPeriod.description' },
          { 
            endDate: endDate.format('MMM DD, YYYY'),
            daysRemaining,
          }
        ),
      })
    }
  }

  return alerts.slice(0, 3) // Maximum 3 alerts
}

/**
 * Get the primary leave type (usually the first active one or most used)
 */
export const getPrimaryLeaveType = (leaveTypes: IUserLeaveTypeResult[]): IUserLeaveTypeResult | undefined => {
  const activeLeaveTypes = leaveTypes.filter(lt => lt.isActive)
  
  if (activeLeaveTypes.length === 0) return undefined
  
  // Sort by position, then by usage
  return activeLeaveTypes.sort((a, b) => {
    if (a.position !== b.position) {
      return a.position - b.position
    }
    return b.usedDays - a.usedDays
  })[0]
}

/**
 * Calculate team availability metrics
 */
export const calculateTeamAvailability = (
  teamMembers: any[],
  teamLeavesToday: any[],
  teamLeavesTomorrow: any[],
  teamLeavesNextWeek: any[]
) => {
  const totalTeamSize = teamMembers.length
  
  if (totalTeamSize === 0) {
    return {
      todayOut: [],
      tomorrowOut: [],
      nextWeekCoverage: { percentage: 100, status: 'good' as const },
    }
  }

  const todayOut = teamLeavesToday.map(leave => ({
    id: leave.user.id,
    name: leave.user.name,
    imageUrl: leave.user.imageUrl,
    isOut: true,
    leaveType: leave.leaveType,
    startDate: leave.startDate,
    endDate: leave.endDate,
    isPartDay: leave.isPartDay,
    partDay: leave.partDay,
  }))

  const tomorrowOut = teamLeavesTomorrow.map(leave => ({
    id: leave.user.id,
    name: leave.user.name,
    imageUrl: leave.user.imageUrl,
    isOut: true,
    leaveType: leave.leaveType,
    startDate: leave.startDate,
    endDate: leave.endDate,
    isPartDay: leave.isPartDay,
    partDay: leave.partDay,
  }))

  // Calculate next week coverage
  const nextWeekOut = teamLeavesNextWeek.length
  const coveragePercentage = Math.round(((totalTeamSize - nextWeekOut) / totalTeamSize) * 100)
  
  let coverageStatus: 'good' | 'warning' | 'critical' = 'good'
  if (coveragePercentage < 50) coverageStatus = 'critical'
  else if (coveragePercentage < 75) coverageStatus = 'warning'

  return {
    todayOut,
    tomorrowOut,
    nextWeekCoverage: {
      percentage: coveragePercentage,
      status: coverageStatus,
    },
  }
}

/**
 * Format leave duration for display
 */
export const formatLeaveDuration = (
  workingDays: number, 
  isPartDay: boolean, 
  formatMessage: IFormatMessageFn
): string => {
  if (isPartDay) return formatMessage({ id: 'dashboard.leaveDuration.partDay' })
  if (workingDays === 1) return formatMessage({ id: 'dashboard.leaveDuration.oneDay' })
  return formatMessage({ id: 'dashboard.leaveDuration.days' }, { days: workingDays })
}

/**
 * Get greeting based on time of day
 */
export const getTimeBasedGreeting = (formatMessage: IFormatMessageFn): string => {
  const hour = dayjs().hour()
  
  if (hour < 12) return formatMessage({ id: 'app.goodMorning' })
  if (hour < 17) return formatMessage({ id: 'app.goodAfternoon' })
  return formatMessage({ id: 'app.goodEvening' })
}

/**
 * Check if user is currently in probation period for a specific leave type
 */
export const isUserInProbationPeriod = (
  userStartDate: string,
  probationPeriods: IUserProbationPeriod[],
  leaveTypeId?: string
): { inProbation: boolean; probationEnd?: string; reason?: string } => {
  if (!probationPeriods || probationPeriods.length === 0) {
    return { inProbation: false }
  }

  const currentDate = dayjs()
  const startDate = dayjs(userStartDate)
  
  // Find active probation periods that apply to this leave type (or all if no leaveTypeId specified)
  const activeProbationPeriods = probationPeriods.filter(period => {
    if (leaveTypeId && !period.leaveTypeIds.includes(leaveTypeId)) {
      return false
    }
    
    const probationEndDate = startDate.add(period.lengthDays, 'days')
    return currentDate.isBefore(probationEndDate)
  })

  if (activeProbationPeriods.length === 0) {
    return { inProbation: false }
  }

  // Return the longest probation period (most restrictive)
  const longestProbation = activeProbationPeriods.reduce((longest, current) => 
    current.lengthDays > longest.lengthDays ? current : longest
  )

  const probationEndDate = startDate.add(longestProbation.lengthDays, 'days')

  return {
    inProbation: true,
    probationEnd: probationEndDate.format('YYYY-MM-DD'),
    reason: longestProbation.name,
  }
}

/**
 * Check if user has any active probation periods (for general dashboard display)
 */
export const hasActiveProbationPeriod = (
  userStartDate: string,
  probationPeriods: IUserProbationPeriod[]
): boolean => {
  return isUserInProbationPeriod(userStartDate, probationPeriods).inProbation
}

/**
 * Get probation period info for a specific leave type
 */
export const getProbationPeriodInfo = (
  userStartDate: string,
  probationPeriods: IUserProbationPeriod[],
  leaveTypeId: string
): { 
  inProbation: boolean
  daysRemaining?: number
  endDate?: string
  reason?: string
} => {
  const probationInfo = isUserInProbationPeriod(userStartDate, probationPeriods, leaveTypeId)
  
  if (!probationInfo.inProbation || !probationInfo.probationEnd) {
    return { inProbation: false }
  }

  const currentDate = dayjs()
  const endDate = dayjs(probationInfo.probationEnd)
  const daysRemaining = endDate.diff(currentDate, 'days')

  return {
    inProbation: true,
    daysRemaining: Math.max(0, daysRemaining),
    endDate: probationInfo.probationEnd,
    reason: probationInfo.reason,
  }
}

/**
 * Calculate next accrual date based on leave type accrual settings
 * This mirrors the logic from accrued-tooltip.tsx
 */
export const calculateNextAccrualDate = (
  leaveType: IUserLeaveTypeResult,
  yearStartDay: number
): Date | null => {
  if (!leaveType.accrualType || leaveType.accrualType === AccrualTypeEnum.none) {
    return null
  }

  const today = new Date()
  const todayString = today.toISOString().split('T')[0]
  const startPeriod = leaveType.accrualPeriodStart
  
  // Need firstEarningDate - check if it exists on the leave type
  const policyFirstEarningDate = leaveType.firstEarningDate
  if (!policyFirstEarningDate) {
    return null
  }

  let policyNextEarningDate: Date | null = null

  try {
    switch (leaveType.accrualType) {
      case AccrualTypeEnum.monthly: {
        const { accrualPeriodEnd } = getDatesForAccrualsMonthly({ today, yearStartDay })
        policyNextEarningDate = getPayoutDateForMonthlyPeriod(
          accrualPeriodEnd,
          policyFirstEarningDate,
          todayString
        )
        if (policyNextEarningDate && dayjs(policyNextEarningDate).isBefore(today)) {
          policyNextEarningDate = dayjs(policyNextEarningDate).add(1, 'month').toDate()
        }
        break
      }
      case AccrualTypeEnum.semiMonthly: {
        const { accrualPeriodStart: periodStart, accrualPeriodEnd } = getDatesForAccrualsSemiMonthly({ today, yearStartDay })
        policyNextEarningDate = getPayoutDateForSemiMonthlyPeriod({
          periodStart,
          periodEnd: accrualPeriodEnd,
          firstEarningDate: policyFirstEarningDate,
          accrualPeriodStart: todayString,
          firstPayoutDateOnOrAfterThisDate: todayString,
        })
        break
      }
      case AccrualTypeEnum.biWeekly: {
        const { accrualPeriodStart: periodStart, accrualPeriodEnd } = getDatesForAccrualsBiWeekly({ today, accrualPeriodStart: startPeriod })
        policyNextEarningDate = getPayoutDateForBiWeeklyPeriod(
          periodStart,
          accrualPeriodEnd,
          policyFirstEarningDate,
          todayString
        )
        break
      }
      case AccrualTypeEnum.weekly: {
        const { accrualPeriodStart: periodStart, accrualPeriodEnd } = getDatesForAccrualsWeekly({ today, accrualPeriodStart: startPeriod })
        policyNextEarningDate = getPayoutDateForWeeklyPeriod(
          periodStart,
          accrualPeriodEnd,
          policyFirstEarningDate,
          periodStart
        )
        break
      }
      default:
        return null
    }
  } catch (error) {
    console.warn('Error calculating next accrual date:', error)
    return null
  }

  return policyNextEarningDate
}